<script setup>
import AppLayout from '@/Layouts/AppLayout.vue'
import { CheckCircleIcon } from '@heroicons/vue/20/solid'
import HeadingTwo from '@/Components/HeadingTwo.vue'

defineProps({
    admin: Object,
})

const whyWeExist = [
    'Začalo to jednoduchou otázkou: proč je daňové přiznání tak složité, když může být snadné?',
    'Jako podnikatelé jsme čelili frustraci z nepřehledných formulářů a stresu, zda jsme vše udělali správně.',
    'Mnohokrát jsme se ptali, proč neexistuje služba, která by usnadnila daně pro každého, kdo je nechce řešit sám. A tak jsme se rozhodli nabídnout řešení. Vzniklo priznanisnadno.cz.',
]

const ourExperience = [
    'Dlouholeté zkušenosti: Jsme tým, který má za sebou léta praxe v oblasti daní a účetnictví.',
    'Praxe z první ruky: My sami jsme podnikatelé, kteří prošli každým krokem procesu daňového přiznání. Víme, jaké to je cítit se ztraceni v moři formulářů a předpisů.',
    'Praktické řešení: Z naší zkušenosti víme, že daňové přiznání může být jednoduché a rychlé, když víte, jak na to. A proto jsme vytvořili systém, který vám ušetří čas a stres.',
    'Odborný tým: Naši odborníci mají hluboké znalosti daňových předpisů a aktuálních změn ve legislativě. Můžete se na nás spolehnout, že vaše přiznání bude v souladu se všemi zákony.',
]

const whatSetsUsApart = [
    'Snadnost: Vše jsme navrhli tak, aby to zvládl každý, bez ohledu na to, jaké má zkušenosti s daněmi.',
    'Rychlost: Díky našemu online systému zvládnete vše během pár minut, bez potřeby osobní schůzky.',
    'Odbornost: Za našimi službami stojí odborníci s praktickými zkušenostmi, kteří znají nejen zákony, ale i každodenní výzvy podnikatelů.',
    'Srozumitelnost: Žádné složité právní termíny. Vysvětlíme vám vše tak, abyste přesně věděli, co se děje a proč.',
    'Zákaznický servis: Jsme tu pro vás, kdykoliv budete potřebovat radu nebo pomoc. Naše komunikace je přátelská, přehledná a rychlá.',
    'Spolehlivost: Můžete se na nás spolehnout, že vaše daňové přiznání bude vyřízeno správně a včas. Nemáme žádné skryté poplatky ani zbytečné překážky.',
]

const whoWeAreFor = [
    'Podnikatelé: Ať už máte paušální výdaje, nebo vedete daňovou evidenci, naše služba vám ušetří čas a zajistí správnost přiznání.',
    'Zaměstnanci: Máte více pracovních poměrů nebo jiné příjmy? S námi získáte přehled a pomoc při správném zpracování daní.',
    'Každý, kdo si váží svého času: Pokud nechcete trávit hodiny nad papírováním a chcete se soustředit na to, co je pro vás důležité, jsme tu pro vás.',
]
</script>

<template>
    <AppLayout>
        <template #content>
            <section class="py-12 sm:py-24">
                <div class="mx-auto max-w-3xl px-6 lg:px-8">
                    <HeadingTwo
                        class="!mt-0"
                        value="Proč jsme vznikli?"
                    />

                    <dl class="mt-6 space-y-4 text-zinc-400">
                        <p
                            v-for="(item, index) in whyWeExist"
                            :key="index"
                        >
                            {{ item }}
                        </p>
                    </dl>

                    <div class="mt-10 max-w-2xl">
                        <HeadingTwo value="Naše zkušenosti" />
                        <ul
                            role="list"
                            class="mt-8 max-w-xl space-y-4"
                        >
                            <li
                                v-for="(item, index) in ourExperience"
                                :key="index"
                                class="flex gap-x-3"
                            >
                                <CheckCircleIcon
                                    class="mt-1 size-5 flex-none text-cyan-600"
                                    aria-hidden="true"
                                />
                                <span class="text-zinc-700 dark:text-zinc-400">
                                    <strong class="font-semibold text-zinc-800 dark:text-zinc-300">{{ item.split(': ')[0] }}:</strong>
                                    {{ item.split(': ')[1] }}
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div class="mt-10 max-w-2xl">
                        <HeadingTwo value="Co nás odlišuje" />
                        <ul
                            role="list"
                            class="mt-8 max-w-xl space-y-4"
                        >
                            <li
                                v-for="(item, idx) in whatSetsUsApart"
                                :key="idx"
                                class="flex gap-x-3"
                            >
                                <CheckCircleIcon
                                    class="mt-1 size-5 flex-none text-cyan-600"
                                    aria-hidden="true"
                                />
                                <span class="text-zinc-700 dark:text-zinc-400">
                                    <strong class="font-semibold text-zinc-800 dark:text-zinc-300">{{ item.split(': ')[0] }}:</strong>
                                    {{ item.split(': ')[1] }}
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div class="mt-10 max-w-2xl">
                        <HeadingTwo value="Pro koho tu jsme?" />
                        <ul
                            role="list"
                            class="mt-8 max-w-xl space-y-4"
                        >
                            <li
                                v-for="(item, index) in whoWeAreFor"
                                :key="index"
                                class="flex gap-x-3"
                            >
                                <CheckCircleIcon
                                    class="mt-1 size-5 flex-none text-cyan-600"
                                    aria-hidden="true"
                                />
                                <span class="text-zinc-700 dark:text-zinc-400">
                                    <strong class="font-semibold text-zinc-800 dark:text-zinc-300">{{ item.split(': ')[0] }}:</strong>
                                    {{ item.split(': ')[1] }}
                                </span>
                            </li>
                        </ul>
                    </div>

                    <div class="mt-10 max-w-2xl">
                        <figure class="mt-10 border-l border-cyan-600 pl-9">
                            <blockquote class="font-semibold dark:text-zinc-300">
                                <p>
                                    A jsme tu, abychom vám ušetřili čas, nervy a pomohli vyřešit daně snadno a bez stresu. Děkujeme, že nám
                                    důvěřujete. 😊
                                </p>
                            </blockquote>
                            <figcaption class="mt-6 flex gap-x-4">
                                <img
                                    class="size-6 flex-none rounded-full"
                                    src="https://ui-avatars.com/api/?name=S+C&color=7F9CF5&background=EBF4FF"
                                    alt="SolveCount"
                                />

                                <div class="text-sm/6">
                                    <strong class="font-semibold text-black dark:text-zinc-200">SolveCount s.r.o.</strong>
                                </div>
                            </figcaption>
                        </figure>
                    </div>
                </div>
            </section>
        </template>
    </AppLayout>
</template>
